"use client";

import { JSX, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { scrollNearestScrollerX } from "@/lib/scroll-into-view-x";
import { useViewerLanguage } from "@/lib/i18n";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import {
  useHoverSelect,
  useLegendHoverSelect,
  useSelectionGroup,
} from "@/lib/hooks/use-hover-select";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { CascadeIn } from "@/components/motion/cascade-in";
import { SegmentTabs } from "@/components/motion/segment-tabs";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import type { ModuleKeywordsGroupedData } from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";
import {
  availableKeywordDimensions,
  buildKeywordBuckets,
  KeywordChip,
  keywordDimensionLabels,
  keywordGroupColors,
  keywordWeightLabels,
  normalizeKeywords,
  resolveKeywordColor,
  variantFromWeight,
  type KeywordDimension,
} from "./keyword-shared";
import { accentChartColors } from "./module-accent";

export default function ModuleKeywordsGrouped({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleKeywordsGroupedData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const items = data.items;
  const groups = data.groups;
  const otherLabel = data.otherLabel ?? t("Other");
  const weightLabels = useMemo(() => keywordWeightLabels(t), [t]);
  const dimensionLabels = useMemo(() => keywordDimensionLabels(t), [t]);

  const list = useMemo(() => normalizeKeywords(items), [items]);
  const groupColors = useMemo(
    () => keywordGroupColors(groups, accentChartColors(grayscale, accent)),
    [groups, grayscale, accent],
  );
  const dimensions = useMemo(
    () => availableKeywordDimensions(list, groups, otherLabel, weightLabels),
    [list, groups, otherLabel, weightLabels],
  );

  const [dimension, setDimension] = useState<KeywordDimension>(
    dimensions[0] ?? "group",
  );
  const selection = useSelectionGroup();
  const keyword = useHoverSelect<string>({ group: selection });
  const selected = keyword.selected;
  const hasHover = useHoverCapable();
  const legend = useLegendHoverSelect({ group: selection });
  const legActive = legend.selected;
  const chipRefs = useRef<Record<string, HTMLElement | null>>({});

  const filterDimension: KeywordDimension =
    dimension === "group" ? "weight" : "group";

  const columns = useMemo(
    () =>
      buildKeywordBuckets(list, dimension, groups, otherLabel, weightLabels).map((b) => ({
        ...b,
        items: [...b.items].sort((a, c) => c.weight - a.weight),
      })),
    [list, dimension, groups, otherLabel, weightLabels],
  );

  const filterBuckets = useMemo(
    () => buildKeywordBuckets(list, filterDimension, groups, otherLabel, weightLabels),
    [list, filterDimension, groups, otherLabel, weightLabels],
  );

  const legendItems = filterBuckets.map((b) => ({
    id: b.id,
    label: stripInlineMarkdown(b.label),
    value: b.items.length,
    color:
      b.weight !== undefined
        ? "var(--muted)"
        : (groupColors[b.id] ?? "var(--muted)"),
    swatchVariant:
      b.weight !== undefined ? variantFromWeight(b.weight) : undefined,
  }));

  const activeFilter = legend.filter;
  const filterIds = useMemo(() => {
    const bucket =
      activeFilter !== null ? filterBuckets[activeFilter] : undefined;
    return bucket ? new Set(bucket.items.map((k) => k.id)) : null;
  }, [filterBuckets, activeFilter]);

  const legendSelectedIndex = useMemo(() => {
    if (legActive !== null) return legActive;
    if (selected === null) return null;
    const i = filterBuckets.findIndex((b) =>
      b.items.some((k) => k.id === selected),
    );
    return i === -1 ? null : i;
  }, [legActive, selected, filterBuckets]);

  const onDimensionChange = (next: string | null) => {
    if (next === null) return;
    setDimension(next as KeywordDimension);
    legend.setSelected(null);
    legend.setHovered(null);
  };

  const onSelectFilter = (i: number) => {
    legend.select(i);
    const first = filterBuckets[i]?.items[0];
    if (first) {
      scrollNearestScrollerX(chipRefs.current[first.id], {
        align: "nearest",
        once: false,
      });
    }
  };

  const focused = list.find((k) => k.id === keyword.effective);

  return (
    <div
      className={cn("w-full", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      {dimensions.length > 1 && (
        <div className="mb-4 w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <SegmentTabs
            value={dimension}
            onValueChange={onDimensionChange}
            options={dimensions.map((d) => ({
              value: d,
              label: dimensionLabels[d],
            }))}
          />
        </div>
      )}

      <div className="w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <CascadeIn
          keySignature={dimension}
          className="grid grid-flow-col auto-cols-[280px] gap-x-6"
        >
          {columns.map((col) => (
            <div key={col.id}>
              <div className="mb-3 flex items-baseline justify-between border-b border-dashed border-[var(--border-dashed)] pb-2">
                <span
                  className={`text-sm text-[var(--muted)] ${MODULE_LABEL_WRAP.cell}`}
                >
                  {stripInlineMarkdown(col.label)}
                </span>
                <span className="text-xs tabular-nums text-[var(--subtle)]">
                  {col.items.length}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {col.items.map((k) => {
                  const isHovered = keyword.effective === k.id;
                  const isSelected = selected === k.id;
                  const dim =
                    (filterIds !== null && !filterIds.has(k.id)) ||
                    (keyword.effective !== null && !isHovered);
                  return (
                    <span
                      key={k.id}
                      ref={(el) => {
                        chipRefs.current[k.id] = el;
                      }}
                      className="inline-flex"
                    >
                      <KeywordChip
                        text={stripInlineMarkdown(k.text)}
                        weight={k.weight}
                        variant={k.variant}
                        color={resolveKeywordColor(k, groupColors)}
                        ariaPressed={isSelected}
                        state={isHovered || isSelected ? "hover" : null}
                        dim={dim}
                        onMouseEnter={hasHover ? () => keyword.setHovered(k.id) : undefined}
                        onMouseLeave={hasHover ? () => keyword.setHovered(null) : undefined}
                        onClick={(e) =>
                          keyword.select(k.id, {
                            kind: "card",
                            id: k.id,
                            element: e.currentTarget,
                          })
                        }
                      />
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </CascadeIn>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-dashed border-[var(--border-dashed)] pt-4">
        {legendItems.length > 1 && (
          <SwipeScroll className="min-w-0 flex-1">
            <Legend
              items={legendItems}
              hoveredIndex={legend.hovered}
              onHoverChange={legend.setHovered}
              selectedIndex={legendSelectedIndex}
              onSelect={onSelectFilter}
              onLabelRef={legend.setLabelRef}
              className="flex w-max flex-row gap-2"
            >
              <LegendItem className="flex shrink-0 items-center gap-2">
                <LegendMarker
                  variant={filterDimension === "weight" ? "swatch" : "dot"}
                />
                <LegendLabel className="text-sm font-medium text-[var(--fg)]" />
                <LegendValue className="text-sm tabular-nums text-[var(--subtle)]" />
              </LegendItem>
            </Legend>
          </SwipeScroll>
        )}
        <div className="min-h-5 px-2.5 text-sm">
          {focused ? (
            <span className="text-[var(--muted)]">
              <span
                className={`font-medium text-[var(--fg)] ${MODULE_LABEL_WRAP.label}`}
              >
                {stripInlineMarkdown(focused.text)}
              </span>
              {" · "}
              {weightLabels[focused.weight]}
              {focused.slides && focused.slides.length > 0 && (
                <> · {t("Slide {number}", { number: focused.slides.join(", ") })}</>
              )}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
